import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Star, StarHalf, BadgeCheck } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import CountUp from '../components/CountUp';
import { COLLECTIONS, FAQ_ITEMS, REVIEWS } from '../data/mock';
import { useCatalog } from '../context/CatalogContext';
import { useTranslation } from '../hooks/useTranslation';

const HERO_IMAGE = '/images/hero-mobile.jpg';
const HERO_IMAGE_DESKTOP = 'https://static.prod-images.emergentagent.com/jobs/f1670493-e048-4fd6-ac4e-3d295547ccb4/images/acffdb06b7bf8df58978bde42aacebfe180bf7da5eae8f66900965a9573a4c20.jpeg';
const HERO_WATCH_DESKTOP = '/images/hero-watch-desktop.png';

// Curated Best Sellers, fixed order (per merchant request) — NOT auto-derived from the catalog.
const BEST_SELLER_SLUGS = ['wimbledon', 'daytona-noir', 'royal-oak-skeleton-full-black', 'day-date'];

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { el.classList.add('visible'); io.unobserve(el); } });
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

function ReviewCard({ r, i, t }) {
  const fullStars = Math.floor(r.rating);
  const hasHalf = r.rating % 1 !== 0;
  return (
    <div
      data-testid={`review-card-${i}`}
      className="h-full bg-white border border-[#EAE4D8] p-8 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
    >
      <div className="flex items-center gap-1" data-testid={`review-stars-${i}`}>
        {Array.from({ length: fullStars }).map((_, s) => (
          <Star key={s} className="w-4 h-4 fill-gold text-gold" />
        ))}
        {hasHalf && <StarHalf className="w-4 h-4 fill-gold text-gold" />}
      </div>
      <blockquote className="mt-5 text-[#333] leading-relaxed flex-1">
        "{r.quote}"
      </blockquote>
      <div className="mt-6 pt-5 border-t border-[#EAE4D8] flex items-center gap-3">
        {r.photo ? (
          <img src={r.photo} alt={r.name} className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center font-serif text-sm">
            {r.name.charAt(0)}
          </div>
        )}
        <div className="flex-1">
          <div className="text-sm font-medium text-[#1A1A1A]">{r.name}</div>
          <div className="flex items-center gap-1 text-[10px] tracking-wide uppercase text-emerald-600 font-medium" data-testid={`review-verified-badge-${i}`}>
            <BadgeCheck className="w-3 h-3" /> {t('home.reviewVerified')}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { products } = useCatalog();
  const { t, lang } = useTranslation();
  const featured = BEST_SELLER_SLUGS
    .map((slug) => products.find((p) => p.slug === slug))
    .filter(Boolean);
  const faqItems = FAQ_ITEMS[lang] || FAQ_ITEMS.fr;
  const reviews = REVIEWS[lang] || REVIEWS.fr;

  const decimalSeparator = (lang === 'fr' || lang === 'es') ? ',' : '.';
  const STATS = [
    { target: 500, decimals: 0, suffix: '+', l: t('home.statClients') },
    { target: 4.9, decimals: 1, suffix: '/5', l: t('home.statNote') },
    { target: 700, decimals: 0, suffix: '+', l: t('home.statVentes') }
  ];

  return (
    <main>
      {/* HERO - Single immersive image */}
      <section className="relative w-full overflow-hidden bg-black aspect-[3/4] md:aspect-auto md:h-[100vh] md:min-h-[600px] md:max-h-[1100px]">
        {/* Mobile: object-cover with 3:4 aspect matching image ratio — no black bars */}
        <img
          src={HERO_IMAGE}
          alt="MSK Watch"
          className="md:hidden absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
        />
        {/* Desktop: dedicated wide landscape background (no product baked in) */}
        <img
          src={HERO_IMAGE_DESKTOP}
          alt="MSK Watch"
          className="hidden md:block absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
        />
        {/* Desktop: watch overlaid separately — same size/framing/prominence as mobile */}
        <img
          src={HERO_WATCH_DESKTOP}
          alt="MSK Watch"
          className="hidden md:block absolute bottom-0 left-1/2 -translate-x-1/2 h-[64%] lg:h-[68%] w-auto object-contain pointer-events-none select-none"
          style={{ filter: 'drop-shadow(0 30px 40px rgba(0,0,0,0.6))' }}
        />
        {/* Subtle gradient overlay for text legibility */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />

        <div className="relative z-10 h-full flex items-end justify-center pb-10 md:pb-20 lg:pb-24">
          <div className="text-center text-white px-6 max-w-4xl animate-fade-up">
            <h1 className="font-serif text-[30px] md:text-6xl lg:text-7xl xl:text-[80px] leading-[1.05] font-light drop-shadow-[0_2px_20px_rgba(0,0,0,0.9)]">
              {t('home.heroTitle')}
            </h1>
            <div className="mt-6 md:mt-12 flex justify-center">
              <Link
                to="/collections"
                data-testid="home-hero-cta"
                className="group inline-flex items-center gap-4 px-8 md:px-16 py-4 md:py-6 bg-white text-black text-[10px] md:text-[13px] tracking-[0.32em] uppercase font-medium hover:bg-black hover:text-white border border-white transition-all duration-500 shadow-2xl"
              >
                {t('home.heroCta')}
                <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="bg-ivory py-14 md:py-24 border-b border-[#EAE4D8]">
        <div className="max-w-6xl mx-auto px-3 md:px-4 grid grid-cols-3 gap-2 md:gap-8 text-center">
          {STATS.map((s, i) => (
            <Reveal key={s.l} delay={i * 120}>
              <div>
                <div className="font-serif text-[36px] sm:text-5xl md:text-6xl lg:text-7xl text-black font-light leading-none">
                  <CountUp target={s.target} decimals={s.decimals} suffix={s.suffix} decimalSeparator={decimalSeparator} />
                </div>
                <div className="mt-3 md:mt-4 text-[9px] sm:text-[10px] md:text-xs tracking-[0.22em] md:tracking-[0.35em] uppercase text-black/60 leading-tight">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Savoir-faire */}
      <section className="pt-14 pb-24 lg:pt-16 lg:pb-32 bg-ivory">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Reveal>
            <div className="text-[11px] tracking-luxury uppercase text-gold mb-4">{t('home.savoirFaireLabel')}</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight">{t('home.savoirFaireTitle')}</h2>
            <p className="mt-8 text-[#555] leading-relaxed max-w-2xl mx-auto">
              {t('home.savoirFaireText')}
            </p>
            <Link to="/collections" className="inline-flex items-center gap-2 mt-10 text-[11px] tracking-luxury uppercase border-b border-[#1A1A1A] pb-1 hover:border-gold hover:text-gold transition-colors">
              {t('home.savoirFaireLink')} <ChevronRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Découvrez nos collections */}
      <section className="py-20 lg:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <Reveal>
              <div className="text-[11px] tracking-luxury uppercase text-gold mb-4">{t('home.collectionsLabel')}</div>
              <h2 className="font-serif text-4xl md:text-5xl font-light">{t('home.collectionsTitle')}</h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 lg:gap-8">
            {COLLECTIONS.map((c, i) => {
              const count = products.filter((p) => p.collection === c.slug).length;
              return (
                <Reveal key={c.slug} delay={i * 60}>
                  <Link to={`/collections/${c.slug}`} className="group block relative aspect-[3/4] overflow-hidden bg-white zoom-parent" data-testid={`home-collection-card-${c.slug}`}>
                    <img src={c.cover} alt={c.name} className="w-full h-full object-contain p-2 md:p-6 bg-white" />
                    <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 md:p-6 text-white">
                      <div className="font-serif text-lg md:text-3xl leading-tight">{c.name}</div>
                      <div className="flex items-center justify-between mt-2 md:mt-3 gap-2">
                        <span className="text-[8px] md:text-[10px] tracking-[0.22em] md:tracking-[0.32em] uppercase opacity-75 whitespace-nowrap">{count > 0 ? `${count} ${t('home.models')}` : t('home.soon')}</span>
                        <span className="inline-flex items-center gap-1 md:gap-2 text-[8px] md:text-[10px] tracking-[0.22em] md:tracking-[0.32em] uppercase opacity-90 group-hover:gap-2 md:group-hover:gap-3 transition-all duration-500 whitespace-nowrap">
                          {t('home.discover')} <ArrowRight className="w-2.5 h-2.5 md:w-3 md:h-3" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="py-20 lg:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <Reveal>
              <div className="text-[11px] tracking-luxury uppercase text-gold mb-3">{t('home.newLabel')}</div>
              <h2 className="font-serif text-3xl md:text-4xl font-light">{t('home.newTitle')}</h2>
            </Reveal>
            <Link to="/montres" className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase link-underline">
              {t('home.viewAllModels')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {/* Mobile: horizontal scroll (6 items) */}
          <div className="md:hidden -mx-4">
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth px-4 pb-4 no-scrollbar">
              {featured.map((p) => (
                <div key={p.slug} className="snap-start shrink-0 w-[68%] xs:w-[62%]">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
          {/* Desktop: grid — always exactly 4 curated best sellers, evenly aligned */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-8">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={i * 80}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Notre histoire */}
      <section className="py-24 lg:py-32 bg-ivory">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Reveal>
            <div className="text-[11px] tracking-luxury uppercase text-gold mb-6">{t('home.objectifLabel')}</div>
            <p className="font-serif text-[20px] md:text-[26px] leading-relaxed text-[#1A1A1A] font-light">
              {t('home.objectifText1')}
            </p>
            <Link to="/notre-histoire" className="inline-flex items-center gap-2 mt-8 text-[11px] tracking-luxury uppercase border-b border-[#1A1A1A] pb-1 hover:border-gold hover:text-gold transition-colors" data-testid="home-read-more-link">
              {t('home.readMore')} <ChevronRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 lg:py-28 bg-cream" data-testid="home-reviews-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 px-4">
            <Reveal>
              <div className="text-[11px] tracking-luxury uppercase text-gold mb-4">{t('home.testimonialsLabel')}</div>
              <h2 className="font-serif text-4xl md:text-5xl font-light">{t('home.testimonialsTitle')}</h2>
            </Reveal>
          </div>
          {/* Mobile: horizontal swipe carousel, one large card + peek of next */}
          <div className="md:hidden -mx-4">
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth px-4 pb-2 no-scrollbar">
              {reviews.map((r, i) => (
                <div key={r.name} className="snap-start shrink-0 w-[86%]">
                  <ReviewCard r={r} i={i} t={t} />
                </div>
              ))}
            </div>
          </div>
          {/* Desktop: grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-6 lg:gap-8 px-4">
            {reviews.map((r, i) => (
              <Reveal key={r.name} delay={(i % 6) * 100}>
                <ReviewCard r={r} i={i} t={t} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="py-20 lg:py-28 bg-ivory">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-12">
            <Reveal>
              <div className="text-[11px] tracking-luxury uppercase text-gold mb-3">{t('faq.label')}</div>
              <h2 className="font-serif text-4xl md:text-5xl font-light">{t('faq.title')}</h2>
            </Reveal>
          </div>
          <div className="divide-y divide-[#EAE4D8] border-y border-[#EAE4D8]">
            {faqItems.map((item, i) => (
              <details key={i} className="group py-6">
                <summary className="flex justify-between items-center cursor-pointer list-none font-serif text-lg md:text-xl hover:text-gold transition-colors">
                  {item.q}
                  <span className="text-gold text-2xl font-light transition-transform group-open:rotate-45">+</span>
                </summary>
            <p className="mt-4 text-[#555] leading-relaxed text-sm whitespace-pre-line">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-28 lg:py-36 bg-[#1A1A1A] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Reveal>
            <h2 className="font-serif text-5xl md:text-6xl font-light">{t('home.ctaTitle')}</h2>
            <Link to="/collections" className="inline-flex items-center gap-3 mt-12 px-10 py-4 border border-white/70 text-[11px] tracking-luxury uppercase hover:bg-white hover:text-[#1A1A1A] transition-all duration-500">
              {t('home.ctaButton')} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
